import React, { memo, useMemo } from "react"
import { useTranslation } from "react-i18next";
import { RootState, useAppDispatch, useAppSelector } from "../../store/store";
import { GridColDef } from "@mui/x-data-grid";
import moment from "moment";
import { DeleteOptimization, handleToggleDeleteOptimizationDialog } from "../../store/optimization-slice/optimization";
import TableGrid from "../table-grid/TableGrid";
import { EndPoints } from "../../axios/endPoints";
import DeleteDialog from "../common/DeleteDialog";
import { enqueueSnackbar } from "../../store/app-slice/app";
import { handleNotificationVariant } from "../notifications/_common";
import OptimizationDetailsDialog from "./OptimizationDetailsDialog";
import OptimizationStatusActions from "./OptimizationStatusActions";
import FailedOptimizationDialog from "./FailedOptimizationDialog";

interface OptimizationsTableProps {
    optimizationDataId: string | null
}

const OptimizationsTable = ({ optimizationDataId } : OptimizationsTableProps) => {
    const dispatch = useAppDispatch();   
    const { t } = useTranslation();

    const { deleteOptimizationDialog, deletingOptimization, refreshOptimizationsTable, optimizationDetailsDialog } = useAppSelector((state: RootState) => state.optimization);

    const columns : GridColDef[] = useMemo(() => [
        { field: 'id', width: 80, headerName: t('OPTIMIZATIONS_ID_FIELD') },
        { 
            field: 'name', 
            width: 150,
            headerName: t('OPTIMIZATIONS_NAME_FIELD')
        },
        { 
            field: 'processDate', 
            width: 150,
            headerName: t('OPTIMIZATIONS_DATE_FIELD'), 
            renderCell: (params) => { 
                return moment.utc(params.value).local().format('DD/MM/YYYY HH:mm')
            }, 
        },
        { 
            field: 'requestOptimisationColumns', 
            width: 200,
            headerName: t('OPTIMIZATIONS_OPTIMIZED_COLUMNS_FIELD')
        },
        { 
            field: 'actions',
            sortable: false,
            minWidth: 150,
            headerName: t('OPTIMIZATIONS_ACTIONS_FIELD'), 
            renderCell: (params) => {
                return(<OptimizationStatusActions params={params} dispatch={dispatch} t={t} />)
            }
        }
    ], [dispatch, t])
    
    const memoizedQueryParams = useMemo(
        () => ({ modelId: optimizationDataId }),
        [optimizationDataId]
    );

    return(
        <React.Fragment>
            <TableGrid 
                title={t('OPTIMIZATIONS_TABLE')}
                baseURL={EndPoints.OPTIMIZATION_GET_OPTIMIZATIONS}
                aditionalQueryParams={memoizedQueryParams}
                columns={columns}
                sortColumns={[{ field: 'id', sort: 'asc' }]}
                disableRowSelection
                tableHeight={500}
                rowHeight={35}
                columnHeaderHeight={40}
                refresh={refreshOptimizationsTable as number}
            />
            <DeleteDialog 
                open={deleteOptimizationDialog.isOpen}
                title={t('DELETE_OPTIMIZATION_DIALOG_TITLE')} 
                bodyText={t('DELETE_OPTIMIZATION_DIALOG_BODY', { name: deleteOptimizationDialog?.body?.name })} 
                isLoading={deletingOptimization} 
                onClose={function (reason?: string): void {
                    dispatch(handleToggleDeleteOptimizationDialog({ isOpen: false, body: null }));
                }} 
                onSubmit={function (): void {
                    dispatch(DeleteOptimization(deleteOptimizationDialog?.body?.id)).then((res : any) => {
                        if(res && res.payload) { 
                            dispatch(enqueueSnackbar({ 
                                message: t(`${res.payload.message}`),
                                options: { 
                                    variant: handleNotificationVariant(res.payload.success),
                                    persist: !res.payload.success
                                } 
                            }))
                        }
                    });
                }} 
                cancelButtonText={t('DELETE_PREDICTION_DIALOG_CANCEL_BUTTON')} 
                submitButtonText={t('DELETE_PREDICTION_DIALOG_SUBMIT_BUTTON')}        
            />
            { optimizationDetailsDialog && optimizationDetailsDialog.isOpen ? <OptimizationDetailsDialog /> : '' }
            <FailedOptimizationDialog />
        </React.Fragment>
    )
}

export default memo(OptimizationsTable);